import React, {useCallback, useState} from 'react';

import type {Option} from '@pexip/components';
import {
    RangeSlider,
    Button,
    Form,
    Input,
    Select,
    Checkbox,
    InputLabel,
    TextArea,
    RadioButton,
    Text,
    FontVariant,
} from '@pexip/components';
import type {
    FormInput,
    TextArea as ITextArea,
    FormInputId,
    FormInputValue,
    InputElement,
    SelectElement,
    FormElement,
    ChecklistElement,
    ChecklistOption,
    RadioOption,
    RadioGroupElement,
    RangeSliderElement,
} from '@pexip/plugin-api';
import {
    isFormRangeElement,
    isFormInputElement,
    isFormSelectElement,
    isFormChecklistElement,
    isFormRadioGroupElement,
} from '@pexip/plugin-api';

import styles from './Form.module.scss';

export const FormWrapper: React.FC<{
    elements: Record<string, FormElement>;
    submitTitle: string;
    onSubmitInput: (input: FormInput) => void;
}> = ({elements, submitTitle, onSubmitInput}) => {
    const [formElements, setFormElements] = useState<FormInput>(() =>
        Object.entries(elements).reduce((initialState, [id, element]) => {
            if (isFormSelectElement(element)) {
                initialState[id] =
                    element.selected ?? (element.options[0]?.id as string);
            } else if (isFormChecklistElement(element)) {
                initialState[id] = (
                    element.options as ChecklistOption[]
                ).reduce(
                    (accm, option) => {
                        accm[option.id] = option.checked ?? false;
                        return accm;
                    },
                    {} as Record<string, boolean>,
                );
            } else if (isFormInputElement(element)) {
                initialState[id] = element.value;
            } else if (isFormRadioGroupElement(element)) {
                initialState[id] = element.options.find(
                    opt => opt.id === element.checked,
                )?.value;
            } else if (isFormRangeElement(element)) {
                initialState[id] = element.selectedValue ?? 0;
            } else {
                initialState[id] = undefined;
            }
            return initialState;
        }, {} as FormInput),
    );

    const updateFormElement = useCallback(
        (value: FormInputValue, inputId: FormInputId) => {
            setFormElements(updatedInput => {
                return {
                    ...updatedInput,
                    [inputId]: value,
                };
            });
        },
        [],
    );

    const renderElement = (inputId: string, element: FormElement) => {
        switch (element.type) {
            case 'select': {
                return (
                    <SelectWrapper
                        element={element}
                        updateValue={value => updateFormElement(value, inputId)}
                    />
                );
            }
            case 'checklist': {
                return (
                    <ChecklistWrapper
                        element={element}
                        updateValue={value => updateFormElement(value, inputId)}
                    />
                );
            }
            case 'radio': {
                return (
                    <RadioWrapper
                        element={element}
                        updateValue={value => updateFormElement(value, inputId)}
                    />
                );
            }
            case 'range': {
                return (
                    <RangeWrapper
                        element={element}
                        updateValue={value => updateFormElement(value, inputId)}
                    />
                );
            }
            default: {
                return (
                    <InputWrapper
                        element={element}
                        updateValue={value => updateFormElement(value, inputId)}
                    />
                );
            }
        }
    };

    return (
        <Form
            onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                onSubmitInput(formElements);
            }}
        >
            {Object.entries(elements).map(([inputId, element]) => (
                <div key={inputId} className="my-4">
                    {renderElement(inputId, element)}
                </div>
            ))}
            <Button type="submit" modifier="fullWidth" size="large">
                {submitTitle}
            </Button>
        </Form>
    );
};

const SelectWrapper: React.FC<{
    element: SelectElement;
    updateValue: (value: string) => void;
}> = ({element, updateValue}) => {
    const [options] = useState(element.options as Option[]);
    const [selected, setSelected] = useState(
        element.selected ?? (element.options[0]?.id as string),
    );

    const handleChange = useCallback(
        (value: string) => {
            setSelected(value);
            updateValue(value);
        },
        [updateValue],
    );

    return (
        <Select
            autoComplete={element.autoComplete ?? 'off'}
            name={element.name}
            label={element.name}
            value={selected}
            onValueChange={handleChange}
            options={options}
            required={Boolean(!element.isOptional)}
            isFullWidth
        />
    );
};

const InputWrapper: React.FC<{
    element: InputElement | ITextArea;
    updateValue: (value: string) => void;
}> = ({element, updateValue}) => {
    const [input, setInput] = useState(element.value ?? '');

    const handleChange = useCallback(
        (value: string) => {
            setInput(value);
            updateValue(value);
        },
        [updateValue],
    );

    const props = {
        autoComplete: element.autoComplete ?? 'off',
        name: element.name,
        label: element.name,
        onValueChange: handleChange,
        required: Boolean(!element.isOptional),
        value: input,
        placeholder: element.placeholder,
    };

    return element.type === 'textarea' ? (
        <TextArea {...props} />
    ) : (
        <Input type={element.type} {...props} />
    );
};

const ChecklistWrapper: React.FC<{
    element: ChecklistElement;
    updateValue: (value: Record<string, boolean>) => void;
}> = ({element, updateValue}) => {
    const [checked, setChecked] = useState<Record<string, boolean>>(() =>
        (element.options as ChecklistOption[]).reduce<Record<string, boolean>>(
            (state, option) => {
                state[option.id] = option.checked ?? false;
                return state;
            },
            {},
        ),
    );

    const handleChange = useCallback(
        (value: Record<string, boolean>) => {
            setChecked(value);
            updateValue(value);
        },
        [updateValue],
    );

    return (
        <div className={styles.checklistWrapper}>
            <InputLabel text={element.name} variant="standard" />
            {element.options.map(({id, label, required}) => (
                <Checkbox
                    key={id}
                    label={label}
                    name={label}
                    checked={checked[id]}
                    onChange={_e =>
                        handleChange({...checked, [id]: !checked[id]})
                    }
                    required={required}
                />
            ))}
        </div>
    );
};

const RadioWrapper: React.FC<{
    element: RadioGroupElement;
    updateValue: (value: string) => void;
}> = ({element, updateValue}) => {
    const [options] = useState(element.options as RadioOption[]);
    const [checked, setChecked] = useState(element.checked);

    const handleChange = useCallback(
        (optionId: string, checked: boolean) => {
            setChecked(optionId);
            if (checked) {
                const value = options.find(opt => opt.id === optionId)?.value;
                if (value) {
                    updateValue(value);
                }
            }
        },
        [options, updateValue],
    );
    return (
        <div className={styles.radioWrapper}>
            <InputLabel text={element.name} variant="standard" />
            {options.map(({id, label, value, isDisabled}) => (
                <RadioButton
                    key={id}
                    groupName={element.groupName}
                    required={Boolean(!element.isOptional)}
                    label={label}
                    value={value}
                    checked={checked === id}
                    isDisabled={isDisabled}
                    onValueChange={selected => handleChange(id, selected)}
                />
            ))}
        </div>
    );
};

const RangeWrapper: React.FC<{
    element: RangeSliderElement;
    updateValue: (value: number) => void;
}> = ({element, updateValue}) => {
    const [value, setValue] = useState(() => element.selectedValue ?? 0);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = Number(e.target.value);
            setValue(value);
            updateValue(value);
        },
        [updateValue],
    );

    return (
        <div className={styles.rangeWrapper}>
            <InputLabel text={element.name} variant="standard" />
            <div>
                <RangeSlider
                    selectedValue={value}
                    min={element.min}
                    max={element.max}
                    step={element.step}
                    onChange={onChange}
                />
                <Text className="ml-1" fontVariant={FontVariant.Body}>
                    {value}
                </Text>
            </div>
        </div>
    );
};
